import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Target, Award, Globe } from 'lucide-react';

const AboutUs = () => {
        // Scroll to top on mount
        useEffect(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
        }, []);

        return (
                <div className="min-h-screen bg-gradient-to-br from-[#f8faff] via-[#bed3ff]/30 to-[#e8f0ff]">
                        {/* Back Button and Logo */}
                        <div className="max-w-[1400px] mx-auto px-8 lg:px-16 pt-15">
                                <div className="flex items-center justify-between">
                                        <Link to="/">
                                                <motion.button
                                                        className="flex items-center gap-2 text-[#004aad] hover:text-[#0066cc] transition-colors"
                                                        whileHover={{ x: -5 }}
                                                >
                                                        <ArrowLeft className="w-5 h-5" />
                                                        <span className="font-semibold">Back to Home</span>
                                                </motion.button>
                                        </Link>

                                        {/* Circular Logo */}
                                        <motion.div
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ duration: 0.5 }}
                                        >
                                                <img
                                                        src="https://customer-assets.emergentagent.com/job_security-portfolio-4/artifacts/yeywo8ds_INVeNTi.png"
                                                        alt="Inventia Logo"
                                                        className="h-16 w-16 object-cover rounded-full shadow-lg"
                                                />
                                        </motion.div>
                                </div>
                        </div>

                        {/* Hero Section */}
                        <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="max-w-[1400px] mx-auto px-8 lg:px-16 py-20 pt-10"
                        >
                                <h1 className="text-6xl font-bold text-[#1a1a2e] mb-6">About Inventia</h1>
                                <p className="text-2xl text-[#4a5568] max-w-3xl">
                                        Building the Future of Secure Digital Infrastructure, One Enterprise at a Time.
                                        At Inventia, we are shaping the future of cybersecurity and IT solutions by providing cutting-edge enterprise-grade security and IT infrastructure services. Our mission is to empower organizations by offering secure digital infrastructure and RFID systems designed for innovation, scalability, and compliance.
                                </p>

                        </motion.div>

                        {/* Content Sections */}
                        <div className="max-w-[1400px] mx-auto px-8 lg:px-16 pb-20 space-y-20">

                                {/* Mission Section */}
                                <motion.div
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.2 }}
                                        className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
                                >
                                        <div>
                                                <div className="flex items-center gap-3 mb-4">
                                                        <Target className="w-8 h-8 text-[#004aad]" />
                                                        <h2 className="text-4xl font-bold text-[#1a1a2e]">Our Mission</h2>
                                                </div>
                                                <p className="text-lg text-[#4a5568] leading-relaxed mb-6">
                                                        To Empower Enterprises with Cutting-Edge Security Solutions.
                                                        Our mission is to protect digital assets, optimize enterprise IT infrastructures, and accelerate technological transformation for businesses worldwide. We provide a reliable foundation for organizations to scale confidently, ensuring that security and innovation are seamlessly integrated.
                                                </p>

                                        </div>
                                        <div className="bg-gradient-to-br from-white to-[#f0f5ff] rounded-2xl p-12 shadow-xl border border-[#004aad]/10">
                                                <div className="space-y-6">
                                                        {['15+ Years Experience', '250+ Enterprise Clients', '99.9% Uptime Guarantee', '24/7 Global Support'].map((item, index) => (
                                                                <motion.div
                                                                        key={index}
                                                                        initial={{ opacity: 0, x: -20 }}
                                                                        animate={{ opacity: 1, x: 0 }}
                                                                        transition={{ delay: 0.4 + index * 0.1 }}
                                                                        className="flex items-center gap-3"
                                                                >
                                                                        <div className="w-2 h-2 bg-[#004aad] rounded-full" />
                                                                        <span className="text-[#1a1a2e] font-semibold">{item}</span>
                                                                </motion.div>
                                                        ))}
                                                </div>
                                        </div>
                                </motion.div>

                                {/* Values Section */}
                                <motion.div
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.4 }}
                                >
                                        <div className="flex items-center gap-3 mb-12 justify-center">
                                                <Award className="w-8 h-8 text-[#004aad]" />
                                                <h2 className="text-4xl font-bold text-[#1a1a2e]">Our Values</h2>
                                        </div>
                                        <p className="text-xl text-[#4a5568] max-w-3xl mx-auto mb-12">
                                                At Inventia, we uphold a commitment to innovation, integrity, and excellence in every aspect of our business. Our cybersecurity solutions and RFID technology are crafted to address the emerging threats and challenges of today’s fast-evolving digital landscape, ensuring maximum security, trust, and operational excellence for our clients.
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                {[
                                                        { title: 'Innovation', desc: 'Continuously advancing technology to stay ahead of emerging threats and opportunities.' },
                                                        { title: 'Integrity', desc: 'Operating with transparency, honesty, and unwavering ethical standards in all we do.' },
                                                        { title: 'Excellence', desc: 'Delivering exceptional quality and service that exceeds expectations every time.' }
                                                ].map((value, index) => (
                                                        <motion.div
                                                                key={index}
                                                                initial={{ opacity: 0, y: 20 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                transition={{ delay: 0.6 + index * 0.1 }}
                                                                whileHover={{ y: -5 }}
                                                                className="bg-gradient-to-br from-white to-[#f8faff] rounded-2xl p-8 shadow-lg border border-[#004aad]/10"
                                                        >
                                                                <h3 className="text-2xl font-bold text-[#1a1a2e] mb-4">{value.title}</h3>
                                                                <p className="text-[#4a5568]">{value.desc}</p>
                                                        </motion.div>
                                                ))}
                                        </div>
                                </motion.div>

                                {/* Team Section */}
                                <motion.div
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.6 }}
                                        className="text-center"
                                >
                                        <div className="flex items-center gap-3 mb-6 justify-center">
                                                <Users className="w-8 h-8 text-[#004aad]" />
                                                <h2 className="text-4xl font-bold text-[#1a1a2e]">Our Team</h2>
                                        </div>
                                        <p className="text-xl text-[#4a5568] max-w-3xl mx-auto mb-12">
                                                Security Experts, Engineers, and Innovators at Your Service.
                                                Our team of skilled cybersecurity experts and IT engineers works diligently to protect and optimize your digital infrastructure. United by a passion for technology and a commitment to excellence, we continuously push the boundaries of what's possible to offer next-gen IT services and solutions.

                                        </p>



                                        <div className="bg-gradient-to-r from-[#004aad] to-[#0066cc] rounded-2xl p-12 text-white">
                                                <h3 className="text-3xl font-bold mb-4">Join Our Team</h3>
                                                <p className="text-xl mb-8 opacity-90">We're always looking for talented individuals to join our mission.</p>
                                                <Link to="/careers">
                                                        <button className="bg-white text-[#004aad] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                                                                View Open Positions
                                                        </button>
                                                </Link>
                                        </div>
                                </motion.div>

                                {/* Global Presence */}
                                <motion.div
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.8 }}
                                        className="text-center"
                                >
                                        <div className="flex items-center gap-3 mb-6 justify-center">
                                                <Globe className="w-8 h-8 text-[#004aad]" />
                                                <h2 className="text-4xl font-bold text-[#1a1a2e]">Global Presence</h2>
                                        </div>
                                        <p className="text-xl text-[#4a5568] max-w-3xl mx-auto mb-12">
                                                Serving clients across 45 countries with local expertise and global standards.
                                                With a footprint in North America, Europe, Asia Pacific, and the Middle East, Inventia combines global expertise with a deep understanding of regional cybersecurity challenges. We offer 24/7 support and maintain local teams equipped with the latest industry knowledge to ensure the best solutions for your enterprise security needs.
                                        </p>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                                {[
                                                        { region: 'North America', offices: '5 Offices' },
                                                        { region: 'Europe', offices: '8 Offices' },
                                                        { region: 'Asia Pacific', offices: '6 Offices' },
                                                        { region: 'Middle East', offices: '3 Offices' }
                                                ].map((location, index) => (
                                                        <motion.div
                                                                key={index}
                                                                whileHover={{ scale: 1.05 }}
                                                                className="bg-white rounded-xl p-6 shadow-md border border-[#004aad]/10"
                                                        >
                                                                <div className="text-3xl font-bold text-[#004aad] mb-2">{location.offices.split(' ')[0]}</div>
                                                                <div className="text-sm text-[#718096] uppercase">{location.region}</div>
                                                        </motion.div>
                                                ))}
                                        </div>
                                </motion.div>
                        </div>
                </div>
        );
};

export default AboutUs;